/**
 * @module app.shared
 */

import { autoinject, observable, Container } from 'aurelia-framework';
import { Router } from 'aurelia-router';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { EnvironmentService } from 'app/shared/services/environment-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { noop } from 'app/shared/lib/noop';
import { AccountContext } from 'app/core/lib/account-context';
import { EventAggregator } from 'aurelia-event-aggregator';
import { State } from 'app/shared/lib/state';
import { AppRefocusEvent } from 'app/shared/lib/events';

export { State } from './state';

//TODO: Clean up this file! After we are sure none of this stuff is needed anymore.
@autoinject()
export abstract class ComponentBase {

    @observable
    public state: State;

    protected readonly env!: EnvironmentService;
    protected readonly accountContext!: AccountContext;
    protected router!: Router;
    protected readonly logger!: AppLogger;

    // Inject commonly used objects into base class
    constructor(
        private readonly baseEvent?: EventAggregator,
        env?: EnvironmentService,
        accountContext?: AccountContext,
        router?: Router,
        // ReSharper disable once InconsistentNaming
        logger?: AppLogger) {

        this.state = new State();
        this.env = env as any;
        this.accountContext = accountContext!;
        this.router = router as any;
        this.logger = logger || Container.instance.get(AppLogger);
        this.logger.source = this;
    }

    protected attached(): void {
        // NOTE: [EN] Calling for app to focus when content has loaded.
        if(this.baseEvent) {
            this.baseEvent.publish(new AppRefocusEvent());
        }
    }

    public resolveUrl(url: string): string {
        return this.env.resolveUrl(url);
    }

    private async do(type: StateActionType, action: Function, errorMessage?: string) {
        const endHandler = `end${type}` as 'endLoad' | 'endSave' | 'endUpload' | 'endProcessing';
        const beginHandler = `begin${type}` as 'beginLoad' | 'beginSave' | 'beginUpload' | 'beginProcessing';
        const handleBegin = this.state[beginHandler].bind(this.state);
        const handleEnd = this.state[endHandler].bind(this.state);

        try {
            handleBegin();

            await action();
        } catch(error) {
            if(errorMessage) {
                // we only want to log the error if there is something useful to say with it
                this.logger.error(errorMessage, error);
            }

            throw error;
        }
        finally {
            handleEnd();
        }
    }

    /**
     * Wraps the action in a beginLoad and endLoad calls and logs any errors if you provide it with a message
     * @param action the content to be wrapped by begin and end methods
     * @param errorMessage the error message to be displayed if one is caught
     */
    // TODO: Convert to decorator | JP
    protected async doLoad(action: Function, errorMessage?: string) {
        await this.do(StateActionType.Load, action, errorMessage);
    }

    /**
    * Wraps the action in a beginSave and endSave calls and logs any errors if you provide it with a message
    * @param action the content to be wrapped by begin and end methods
    * @param errorMessage the error message to be displayed if one is caught
    */
    // TODO: Convert to decorator | JP
    protected async doSave(action: Function, errorMessage?: string) {
        await this.do(StateActionType.Save, action, errorMessage);
    }

    /**
    * Wraps the action in a beginUpload and endUpload calls and logs any errors if you provide it with a message
    * @param action the content to be wrapped by begin and end methods
    * @param errorMessage the error message to be displayed if one is caught
    */
    // TODO: Convert to decorator | JP
    protected async doUpload(action: Function, errorMessage?: string) {
        await this.do(StateActionType.Upload, action, errorMessage);
    }

    /**
    * Wraps the action in a beginProcess and endProcess calls and logs any errors if you provide it with a message
    * @param action the content to be wrapped by begin and end methods
    * @param errorMessage the error message to be displayed if one is caught
    */
    // TODO: Convert to decorator | JP
    protected async doProcess(action: Function, errorMessage?: string) {
        await this.do(StateActionType.Processing, action, errorMessage);
    }
}

enum StateActionType {
    Load = 'Load',
    Save = 'Save',
    Upload = 'Upload',
    Processing = 'Processing'
}

export interface IForm {
    state: State;
    hasChanges(): boolean;
    submittedChanges?: any;
    submit(): Promise<boolean>;
    cancel?(): Promise<boolean>;
    reset?(): void;
}


export enum SortDirection {
    Ascending = 'ascending',
    Descending = 'descending'
}

export enum SortNulls {
    Default = 'default',
    First = 'first',
    Last = 'last'
}

export interface SortEntry {
    reference: string;
    direction?: SortDirection | null;
    nulls?: SortNulls | null;
    convert?: (a: any) => any;
}

export interface SortGroupEntry {
    reference: string;
    matchValue: string;
}

export interface ISortingOptions {
    onChanged?: () => void;
}

export const defaultSortingOptions: ISortingOptions = {
    onChanged: noop
}

export class Sorting {

    public entries: SortEntry[] = [];

    public groupEntries: SortGroupEntry[] = [];

    protected options: ISortingOptions;

    constructor(options?: Partial<ISortingOptions>) {
        this.options = { ...defaultSortingOptions, ...options };
    }

    protected onChanged(): void {
        if(this.options.onChanged) {
            this.options.onChanged();
        }
    }

    public indexOf(entry: SortEntry | string) {
        const upperReference = (entry.constructor === String ? entry as string : (entry as SortEntry).reference).toUpperCase();
        for(let i = 0; i < this.entries.length; i++) {
            if(this.entries[i].reference.toUpperCase() === upperReference) {
                return i;
            }
        }
        return -1;
    }

    public clear(): boolean {
        if(this.entries.length === 0 && this.groupEntries.length === 0) {
            return false;
        }
        if(this.entries.length) {
            this.entries.splice(0, this.entries.length);
        }
        if(this.groupEntries.length) {
            this.groupEntries.splice(0, this.groupEntries.length);
        }
        this.onChanged();
        return true;
    }

    public set(entry: SortEntry | string): boolean {
        const entryObject: SortEntry = entry.constructor === String ? { reference: entry as string } : (entry as SortEntry);
        const direction = entryObject.direction || SortDirection.Ascending;
        const nulls = entryObject.nulls || SortNulls.Default;
        const currentIndex = this.indexOf(entryObject.reference);
        const currentEntry = currentIndex >= 0 ? this.entries[currentIndex] : null;
        if(this.entries.length === 1 &&
            currentIndex === 0 &&
            currentEntry &&
            currentEntry.direction === direction &&
            currentEntry.nulls === nulls) {
            return false;
        }
        this.entries.splice(0, this.entries.length);
        this.entries.push({
            reference: entryObject.reference,
            direction: direction,
            nulls: nulls
        });
        this.onChanged();
        return true;
    }

    public add(entry: SortEntry | string): boolean {
        const entryObject: SortEntry = entry.constructor === String ? { reference: entry as string } : (entry as SortEntry);
        const currentIndex = this.indexOf(entryObject.reference);

        if(currentIndex > -1) {
            this.entries.splice(currentIndex, 1);
        }

        this.entries.unshift({
            direction: SortDirection.Ascending,
            nulls: SortNulls.Default,
            ...entryObject
        });

        this.onChanged();
        return true;
    }

    public remove(entry: SortEntry | string): boolean {
        const index = this.indexOf(entry);
        if(index === -1) {
            return false;
        }
        this.entries.splice(index, 1);
        this.onChanged();
        return true;
    }

    public toggle(entry: SortEntry | string): boolean {
        const currentIndex = this.indexOf(entry);
        if(currentIndex === -1) {
            return false;
        }
        const currentEntry: SortEntry = this.entries[currentIndex];
        const direction: SortDirection = currentEntry.direction || SortDirection.Ascending;
        currentEntry.direction = direction === SortDirection.Ascending ? SortDirection.Descending : SortDirection.Ascending;
        this.onChanged();
        return true;
    }

}


// obsolete alias
export type StandardLoadOptions = Partial<IQueryLoadOptions>;

export interface IWindowMessage {
    action: string;
    title?: string;
    type?: string;
    hyperlinkID?: string;
    routeURL?: string;
    routeCode?: string;
    queryString?: string;
}
